import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUploadManager } from '@design-stack-vista/upload-components';
import { AssetStatusType } from '@design-stack-vista/vista-assets-sdk';
import { FileProgress, Uppy } from '@uppy/core';
import { DragDrop } from '@uppy/react';
import XHRUpload from '@uppy/xhr-upload';
import { AlertBox, AlertBoxDismissButton, Box, Button, FlexBox, Icon, Spinner, Typography } from '@vp/swan';
import { ValidationState } from '@vp/upload-and-print-validator';
import { useDebugContext } from 'src/hooks/DebugContextHooks';
import { useErrorContext } from 'src/hooks/ErrorContextHooks';
import { useProductContext } from 'src/hooks/ProductContextHooks';
import { useTrackingContext } from 'src/hooks/TrackingContextHooks';
import { useProcessUpload, useUploadContext } from 'src/hooks/UploadContextHooks';
import { useAuth } from 'src/hooks/useAuth';
import { useIsPdfOnly, useValidationContext } from 'src/hooks/ValidationContextHooks';
import { Upload } from 'src/services/UploadAndPrintService';
import { CimpressUpload, getFileUri, getThumbnailUrl, getUploadUrl } from 'src/services/UploadsService';
import { UPLOAD_ERRORS_LOCALE_KEY_MAP, UPLOAD_MAX_SIZE_MB } from 'src/utils/Constants';
import { isSherbertUpload } from 'src/utils/flagUtil';
import { text } from 'src/utils/localizationUtil';
import { addPageAction, noticeError } from 'src/utils/newRelicUtil';
import { createTrackingDataForProduct } from 'src/utils/trackingUtil';
import { formatBytes } from 'src/utils/uploadUtil';
import { getValidationsForProduct } from 'src/utils/validationUtil';
import { SherbertUploader } from './SherbertUploader';
import { sendEventTracking } from '../Analytics';
import '@uppy/core/dist/style.css';
import '@uppy/drag-drop/dist/style.css';
import './uppy.css';

/**
 * The maximum number of milliseconds between upload progress updates.
 * The default of 30 seconds is too small for large uploads and results in a timeout after reaching 100%.
 */
const progressTimeoutMillis = 120 * 1000;

const pdfFileTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
const allFileTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', ...pdfFileTypes];

const pdfFileTypesText = ['pdf', 'docx'];
const allFileTypesText = ['jpg', 'png', 'gif', 'bmp', ...pdfFileTypesText];

const RemoveUploadButton = ({
  removeUpload,
  ariaLabel,
  isMobile,
}: {
  removeUpload: () => void;
  ariaLabel: string;
  isMobile: boolean;
}) => {
  return (
    <FlexBox
      onClick={removeUpload}
      aria-label={ariaLabel}
      title={text('remove-upload-title')}
      justifyContent="center"
      alignItems="center"
      style={{
        cursor: 'pointer',
        border: 'none',
        position: 'absolute',
        display: 'flex',
        top: -18,
        right: isMobile ? -10 : -18, // Shift the Remove button to the left to not got off screen
        height: 36,
        width: 36,
        backgroundColor: 'black',
        borderRadius: 20,
      }}
    >
      <Icon iconType="closeSmall" size="10p" skin="white" />
    </FlexBox>
  );
};

export type UploaderProps = {
  isMobile: boolean;
};

const Uploader = ({ isMobile }: UploaderProps): ReactElement => {
  const { vpAuth } = useAuth();
  const {
    upload,
    setUpload,
    isUploading,
    setIsUploading,
    uploadRestrictionFailed,
    setUploadRestrictionFailed,
  } = useUploadContext();
  const [uploadStartTime, setUploadStartTime] = useState(0);
  const { processUpload, reprocessUpload } = useProcessUpload();
  const { setValidations, isValidating } = useValidationContext();
  const isPdfOnly = useIsPdfOnly();
  const { setError } = useErrorContext();
  const { productInformation, isProductReady } = useProductContext();
  const { trackingConfiguration } = useTrackingContext();
  const [fileUploadProgress, setFileUploadProgress] = useState<FileProgress | null>(null);
  const [isUploaderReady, setIsUploaderReady] = useState(false);
  const [fileUploadPercentage, setFileUploadPercentage] = useState(0);
  const { debugMode } = useDebugContext();
  const { assetStore } = useUploadManager();

  const uppyRef = useRef<Uppy | null>(null);

  const allowedFileTypes = isPdfOnly ? pdfFileTypes : allFileTypes;
  const allowedFileTypesText = isPdfOnly ? pdfFileTypesText : allFileTypesText;

  const fireUploadTracking = () => {
    if (trackingConfiguration && productInformation.mpvId) {
      sendEventTracking({
        ...trackingConfiguration,
        ...createTrackingDataForProduct(productInformation),
        action: 'Upload Design Clicked',
        label: `Upload`,
        category: 'Design - Upload and Print',
        variant: productInformation.selectedOptions,
      });
    }
  };

  const fireSuccessfulUploadTimeTracking = useCallback(() => {
    if (trackingConfiguration && productInformation.mpvId) {
      const uploadDuration = new Date().getTime() - uploadStartTime;
      sendEventTracking({
        ...trackingConfiguration,
        ...createTrackingDataForProduct(productInformation),
        action: 'Upload Completed',
        label: 'Upload',
        category: 'Design - Upload and Print',
        variant: productInformation.selectedOptions,
        eventDetail: JSON.stringify({ uploadDuration }),
      });
    }
  }, [trackingConfiguration, productInformation, uploadStartTime]);

  const fireFailedUploadTimeTracking = useCallback(() => {
    if (trackingConfiguration && productInformation.mpvId) {
      const uploadDuration = new Date().getTime() - uploadStartTime;
      sendEventTracking({
        ...trackingConfiguration,
        ...createTrackingDataForProduct(productInformation),
        action: 'Upload Failed',
        label: 'Upload',
        category: 'Design - Upload and Print',
        variant: productInformation.selectedOptions,
        eventDetail: JSON.stringify({ uploadDuration }),
      });
    }
  }, [trackingConfiguration, productInformation, uploadStartTime]);

  useEffect(() => {
    async function configureUppy() {
      const uppy = new Uppy({
        meta: { type: 'avatar' },
        restrictions: {
          maxNumberOfFiles: 1,
          maxFileSize: UPLOAD_MAX_SIZE_MB * 1024 * 1024,
          allowedFileTypes: allowedFileTypes,
        },
        autoProceed: true,
        logger: {
          debug: console.log,
          warn: console.log,
          error: console.error,
        },
      })
        .on('upload', () => {
          fireUploadTracking();
          setUpload(null);
          setValidations(null);
          setIsUploading(true);
          setError(null);
          setUploadStartTime(new Date().getTime());
        })
        .on('upload-progress', (_file, progress) => {
          setFileUploadProgress(progress);
          const percentage = Math.round((progress.bytesUploaded / progress.bytesTotal) * 100);
          setFileUploadPercentage(percentage);
        })
        .on('upload-success', async (file, response) => {
          const uploadedFiles: Upload[] = response.body.map((upload: CimpressUpload) => {
            const { uploadId, uploadedFileName } = upload;

            return {
              fileUri: getFileUri(uploadId),
              thumbnailUrl: getThumbnailUrl(uploadId),
              uploadedFileName,
            };
          });
          const uploadedFile = uploadedFiles[0];
          fireSuccessfulUploadTimeTracking();

          addPageAction('unp-upload-success', {
            fileSize: file.size,
            product: productInformation,
          });

          setIsUploading(false);
          const success = await processUpload(uploadedFile);
          if (!success) {
            uppy.cancelAll();
          }
        })
        .on('upload-error', async (file, error, response) => {
          const validations = await getValidationsForProduct(productInformation);
          const index = validations.findIndex(v => v.id === 'CorruptValidator');

          validations[index].message =
            response?.status === 500 &&
            UPLOAD_ERRORS_LOCALE_KEY_MAP[response.body.Message as keyof typeof UPLOAD_ERRORS_LOCALE_KEY_MAP]
              ? UPLOAD_ERRORS_LOCALE_KEY_MAP[response.body.Message as keyof typeof UPLOAD_ERRORS_LOCALE_KEY_MAP]
              : 'validations.corrupt file.message';
          validations[index].state = ValidationState.Error;
          setValidations(validations);
          setIsUploading(false);
          error.message = response?.body.Message ? `${error.message}: ${response.body.Message}` : error.message;
          noticeError(error, {
            method: 'upload-error',
            file: { ...file, requestUrl: uploadUrl },
            response,
          });
          fireFailedUploadTimeTracking();

          uppy.cancelAll();
        })
        .on('restriction-failed', () => {
          setUploadRestrictionFailed(true);
        });

      const uploadUrl = await getUploadUrl('image');
      uppy.use(XHRUpload, { endpoint: uploadUrl, timeout: progressTimeoutMillis });

      uppyRef.current = uppy;
      setIsUploaderReady(true);
    }

    if (vpAuth && isProductReady) {
      configureUppy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vpAuth, isProductReady, productInformation, setIsUploaderReady]);

  // Update file allowed files when all resources are available
  if (uppyRef.current) {
    uppyRef.current.setOptions({
      restrictions: { allowedFileTypes },
    });
  }

  const resetUploader = (): void => {
    uppyRef.current?.cancelAll();
    setUpload(null);
    setValidations(null);
    setIsUploading(false);
    setUploadRestrictionFailed(false);
    setFileUploadPercentage(0);
    setFileUploadProgress(null);
  };

  const uploadingAssets = assetStore?.assets.filter(asset => asset.status.type === AssetStatusType.Preparing);
  const asset = uploadingAssets?.[0];

  const uploadProgress = useMemo(() => {
    if (asset?.status.type === AssetStatusType.Preparing) {
      return asset?.status.uploadProgress !== undefined ? Number(asset.status.uploadProgress) : 0;
    }
    return 0;
  }, [asset?.status]);

  const uploader = isSherbertUpload ? (
    <SherbertUploader />
  ) : (
    uppyRef.current && (
      <DragDrop
        uppy={uppyRef.current}
        height={150}
        locale={{
          strings: {
            dropHereOr: text('upload-label'),
          },
        }}
        note={text('validations.file size.title', { maxFileSizeMegabytes: UPLOAD_MAX_SIZE_MB })}
      />
    )
  );

  return (
    <>
      <Box mb="7">
        {!upload && !isUploading && !isValidating ? (
          <>
            {vpAuth && isUploaderReady ? (
              <>
                {uploader}
                <AlertBox
                  skin="error"
                  width="narrow"
                  dismissed={!uploadRestrictionFailed}
                  onRequestDismiss={() => {
                    setUploadRestrictionFailed(false);
                  }}
                >
                  {text('upload-drag-reject', { acceptedFileTypes: allowedFileTypesText, maxMB: UPLOAD_MAX_SIZE_MB })}
                  <AlertBoxDismissButton visuallyHiddenLabel={text('dismiss-alert')} />
                </AlertBox>
              </>
            ) : (
              <FlexBox justifyContent="center" alignItems="center">
                <Spinner accessibleText={text('loading')} size="standard" />
              </FlexBox>
            )}
          </>
        ) : (
          <Box
            backgroundColor="strong"
            padding="7"
            style={{
              height: 191,
              borderRadius: 8,
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: 'var(--swan-base-border-color)',
              overflow: 'visible',
              position: 'relative',
              maxWidth: 450,
            }}
          >
            {(upload || isUploading) && !isValidating && !isSherbertUpload && (
              <RemoveUploadButton
                removeUpload={resetUploader}
                ariaLabel={text('remove-upload-title')}
                isMobile={isMobile}
              />
            )}

            <FlexBox
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              style={{
                width: '100%',
                height: '100%',
              }}
            >
              {upload && !isValidating && (
                <>
                  <img
                    src={upload.thumbnailUrl}
                    style={{ maxHeight: '100%', maxWidth: '100%' }}
                    alt={upload.uploadedFileName}
                  />
                  <Typography marginTop={'5'} fontSize={'xsmall'}>
                    {upload.uploadedFileName}
                  </Typography>
                </>
              )}
              {(isUploading || isValidating) && (
                <FlexBox
                  justifyContent="center"
                  alignItems="center"
                  flexDirection="column"
                  style={{
                    width: '100%',
                    height: '100%',
                    cursor: 'pointer',
                    display: 'flex',
                  }}
                >
                  <>
                    <FlexBox justifyContent="center" alignItems="center">
                      <Spinner accessibleText={text('loading')} size="standard" />
                    </FlexBox>
                    {isUploading && (
                      <>
                        <Typography marginTop={'6'} fontWeight="bold" fontSize={'standard'}>
                          {text('uploading-with-percentage', {
                            percentage: isSherbertUpload ? uploadProgress : fileUploadPercentage,
                          })}
                        </Typography>
                        {fileUploadProgress && (
                          <Typography fontSize={'xsmall'} textColor="subtle">
                            {text('uploading-size-progress', {
                              uploaded: formatBytes(fileUploadProgress.bytesUploaded),
                              total: formatBytes(fileUploadProgress.bytesTotal),
                            })}
                          </Typography>
                        )}
                      </>
                    )}
                    {isValidating && (
                      <Typography marginTop={'6'} fontWeight="bold" fontSize={'standard'}>
                        {text('processing-upload')}
                      </Typography>
                    )}
                    <Typography fontSize={'xsmall'} textColor="subtle">
                      {text('processing-secondary')}
                    </Typography>
                  </>
                </FlexBox>
              )}
            </FlexBox>
          </Box>
        )}
        {debugMode && upload && <Button onClick={reprocessUpload}>Reprocess Upload</Button>}
      </Box>
    </>
  );
};

export default Uploader;
